import { ChevronRight, Menu, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import type { AccountSummary } from "../../shared/api-types";
import { useT } from "../i18n";
import { CrumbProvider, useCrumbs, type Crumb } from "../lib/page-header";
import { NewTourProvider, useNewTour } from "./NewTourModal";
import { Sidebar } from "./Sidebar";
import { UserMenu } from "./UserMenu";

/**
 * Rahmen der App: Seitenleiste über die volle Höhe, daneben die Seite mit
 * eigener Kopfzeile. Die Kopfzeile trägt bewusst keine feste Trennlinie — sie
 * gehört zur Seite; beim Scrollen kommt eine Haarlinie dazu, damit Inhalt nicht
 * sichtbar darunter durchläuft. Der Rundgang-Editor läuft außerhalb dieses
 * Rahmens (eigener Vollbildmodus).
 */
export function AppShell({
  name,
  email,
  account,
  accounts,
  canCreateAccount,
  onSignOut,
}: {
  name: string;
  email: string;
  account: AccountSummary | null;
  accounts: AccountSummary[];
  canCreateAccount: boolean;
  onSignOut: () => void;
}) {
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const t = useT();

  const crumbValue = useMemo(() => ({ crumbs, setCrumbs }), [crumbs]);

  // Jede Navigation schließt die Schublade — sonst verdeckt sie das Ziel
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  // Nur im Rahmen: Platz für die Scrollbar dauerhaft freihalten (siehe styles.css)
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("scrollbar-stable");
    return () => root.classList.remove("scrollbar-stable");
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    document.addEventListener("keydown", onKey);
    // Seite dahinter festhalten, solange die Schublade offen ist. Die
    // verschwindende Scrollleiste würde das Layout sonst springen lassen.
    const body = document.body;
    const prevOverflow = body.style.overflow;
    const prevPadding = body.style.paddingRight;
    const widthBefore = document.documentElement.clientWidth;
    body.style.overflow = "hidden";
    const gap = document.documentElement.clientWidth - widthBefore;
    if (gap > 0) body.style.paddingRight = `${gap}px`;
    return () => {
      document.removeEventListener("keydown", onKey);
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPadding;
    };
  }, [drawerOpen]);

  return (
    <CrumbProvider value={crumbValue}>
      <NewTourProvider>
        <div className="flex">
          {/* Desktop: klebt oben, scrollt nicht mit der Seite weg */}
          <aside className="sticky top-0 hidden h-dvh w-64 shrink-0 border-r border-zinc-200 bg-white lg:block dark:border-zinc-800 dark:bg-zinc-900/40">
            <SidebarContent
              account={account}
              accounts={accounts}
              canCreateAccount={canCreateAccount}
              onNavigate={() => {}}
            />
          </aside>

          {/* Handy/Tablet: Schublade über der Seite. z bleibt unter den Popovers
              (z-40) und dem Modal (z-50) — der Account-Umschalter klappt darin auf. */}
          <div
            className={`fixed inset-0 z-30 bg-zinc-950/40 transition-opacity lg:hidden ${
              drawerOpen ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <aside
            className={`fixed inset-y-0 left-0 z-[35] w-72 border-r border-zinc-200 bg-white transition-transform duration-200 lg:hidden dark:border-zinc-800 dark:bg-zinc-900 ${
              drawerOpen ? "translate-x-0" : "-translate-x-full"
            }`}
            aria-label={t.nav.menu}
            inert={!drawerOpen}
          >
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              aria-label={t.nav.closeMenu}
              className="absolute right-2 top-3 grid size-8 place-items-center rounded-lg text-zinc-400 transition-colors hover:bg-zinc-200/60 hover:text-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
            >
              <X className="size-4" />
            </button>
            <SidebarContent
              account={account}
              accounts={accounts}
              canCreateAccount={canCreateAccount}
              onNavigate={() => setDrawerOpen(false)}
            />
          </aside>

          <div className="flex min-w-0 flex-1 flex-col">
            <header
              className={`sticky top-0 z-20 flex h-14 items-center gap-2 border-b bg-zinc-50/80 px-4 backdrop-blur transition-colors sm:px-6 dark:bg-zinc-950/70 ${
                scrolled
                  ? "border-zinc-200 dark:border-zinc-800"
                  : "border-transparent dark:border-transparent"
              }`}
            >
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                aria-label={t.nav.openMenu}
                className="-ml-1 grid size-9 shrink-0 place-items-center rounded-lg text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 lg:hidden dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
              >
                <Menu className="size-5" />
              </button>
              <Breadcrumb />
              <div className="ml-auto shrink-0">
                <UserMenu name={name} email={email} onSignOut={onSignOut} />
              </div>
            </header>
            <Outlet />
          </div>
        </div>
      </NewTourProvider>
    </CrumbProvider>
  );
}

/** Seitenleiste mit dem Auslöser für „Neue Tour“ — der Dialog hängt im Rahmen. */
function SidebarContent({
  account,
  accounts,
  canCreateAccount,
  onNavigate,
}: {
  account: AccountSummary | null;
  accounts: AccountSummary[];
  canCreateAccount: boolean;
  onNavigate: () => void;
}) {
  const newTour = useNewTour();
  return (
    <Sidebar
      account={account}
      accounts={accounts}
      canCreateAccount={canCreateAccount}
      onNewTour={() => newTour()}
      onNavigate={onNavigate}
    />
  );
}

/**
 * Pfad zur aktuellen Seite. Auf schmalen Schirmen bleibt nur die letzte Krume
 * stehen — dort ist die Kopfzeile schon mit Menüknopf und Benutzermenü belegt.
 */
function Breadcrumb() {
  const crumbs = useCrumbs();
  const t = useT();
  if (crumbs.length === 0) return null;
  return (
    <nav aria-label={t.nav.breadcrumb} className="flex min-w-0 items-center gap-1 text-sm">
      {crumbs.map((crumb, i) => {
        const last = i === crumbs.length - 1;
        return (
          <span
            key={`${crumb.label}-${i}`}
            className={`flex min-w-0 items-center gap-1 ${last ? "" : "hidden sm:flex"}`}
          >
            {i > 0 && (
              <ChevronRight className="size-3.5 shrink-0 text-zinc-300 dark:text-zinc-600" />
            )}
            {crumb.to && !last ? (
              <Link
                to={crumb.to}
                className="truncate text-zinc-500 transition-colors hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
              >
                {crumb.label}
              </Link>
            ) : (
              <span
                className={`truncate ${last ? "font-medium text-zinc-900 dark:text-zinc-100" : "text-zinc-500 dark:text-zinc-400"}`}
              >
                {crumb.label}
              </span>
            )}
          </span>
        );
      })}
    </nav>
  );
}
